import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";

import { HomeNav } from "@/components/home-nav";
import { SiteFooter } from "@/components/site-footer";
import { getProject } from "@/data/projects";
import { getTheme, themeRedirects, themes } from "@/data/themes";

const BASE = "https://www.luizajesusdoprado.com";

export const Route = createFileRoute("/themes/$slug")({
  loader: ({ params }) => {
    const moved = themeRedirects[params.slug];
    if (moved) throw redirect({ to: "/themes/$slug", params: { slug: moved }, statusCode: 301 });
    const theme = getTheme(params.slug);
    if (!theme) throw notFound();
    return { theme };
  },
  head: ({ params, loaderData }) => {
    const t = loaderData?.theme;
    const url = `${BASE}/themes/${params.slug}`;
    if (!t) return { meta: [{ title: "Unavailable" }, { name: "robots", content: "noindex" }] };
    return {
      meta: [
        { title: t.seoTitle },
        { name: "description", content: t.description },
        { property: "og:title", content: t.seoTitle },
        { property: "og:description", content: t.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${t.title} — Luiza Jesus do Prado`,
            description: t.description,
            url,
            about: t.title,
            author: { "@id": `${BASE}/#person` },
            hasPart: t.projects.map((s) => ({ "@type": "CreativeWork", url: `${BASE}/work/${s}` })),
          }),
        },
      ],
    };
  },
  component: ThemePage,
});

function ThemePage() {
  const { theme } = Route.useLoaderData();
  const items = theme.projects.map(getProject).filter((p) => !!p);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <HomeNav />
      <main id="main-content" className="mx-auto max-w-[1400px] px-6 pb-24 pt-32 md:px-12">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-bone/70">
          Luiza Jesus do Prado · Theme
        </p>
        <h1 className="mt-6 font-didone text-[clamp(2.6rem,7vw,6rem)] leading-[0.95]">{theme.title}</h1>
        {theme.keywords ? (
          <p className="mt-6 font-sans text-sm text-bone/70">{theme.keywords}</p>
        ) : null}
        <p className="mt-10 max-w-3xl font-sans text-base font-light leading-relaxed text-bone/85">{theme.intro}</p>

        {theme.texts && theme.texts.length > 0 ? (
          <section className="mt-16 border-t border-bone/15 pt-8">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.35em] text-bone/70">Texts</h2>
            <ul className="mt-6 divide-y divide-bone/10">
              {theme.texts.map((x) => (
                <li key={x.href}>
                  <a href={x.href} target="_blank" rel="noopener noreferrer" className="flex min-h-11 items-center justify-between py-3 font-sans text-base hover:text-red">
                    {x.label} <span aria-hidden="true">↗</span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <h2 className="mt-16 border-t border-bone/15 pt-8 font-mono text-[10px] uppercase tracking-[0.35em] text-bone/70">Works</h2>
        <ul className="mt-8 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <li key={p.slug}>
              <Link to="/work/$slug" params={{ slug: p.slug }} className="group block">
                <div className="flex h-64 items-center justify-center overflow-hidden bg-bone/5">
                  <img src={p.cover} alt={`${p.title} — Luiza Jesus do Prado`} loading="lazy" className="h-full w-full object-contain" />
                </div>
                <h3 className="mt-4 font-sans text-lg uppercase tracking-tight group-hover:text-red">{p.title}</h3>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/65">{p.year}</p>
              </Link>
            </li>
          ))}
        </ul>

        <nav className="mt-20 border-t border-bone/15 pt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/60">
          <span className="text-bone/65">Other themes</span>{": "}
          {themes
            .filter((t) => t.slug !== theme.slug)
            .map((t) => (
              <Link key={t.slug} to="/themes/$slug" params={{ slug: t.slug }} className="mr-6 hover:text-bone">
                {t.title}
              </Link>
            ))}
        </nav>
      </main>
      <SiteFooter />
    </div>
  );
}
